import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown, DropdownProps } from 'primereact/dropdown';
import TnkButton from '../TnkButton';
// Dropdown is a class component — ref gives back a Dropdown instance with show/hide/focus/clear

import './tnk-dropdown.scss';

/**
 * Wrapper of PrimeReact Dropdown with consistent disabled-state styling.
 * Closes the panel when a scrollable ancestor scrolls (prevents dropdown from drifting out of table viewport).
 */
const TnkDropdown = React.forwardRef<Dropdown, DropdownProps>(
  ({ className, panelClassName, onShow, onHide, filter = true, ...props }, externalRef) => {
    const internalRef = useRef<Dropdown | null>(null);
    const [isOverlayVisible, setIsOverlayVisible] = useState(false);

    // Merge internal ref with external ref so both callers work correctly
    const mergedRef = useCallback(
      (node: Dropdown | null) => {
        internalRef.current = node;
        if (typeof externalRef === 'function') externalRef(node);
        else if (externalRef) externalRef.current = node;
      },
      [externalRef]
    );

    // Close panel when a scrollable ancestor scrolls (prevents dropdown from drifting out of table viewport)
    useEffect(() => {
      if (!isOverlayVisible) return;

      const handleScroll = () => internalRef.current?.hide?.();

      const el = internalRef.current?.getElement?.() as HTMLElement | null;
      const scrollableParents: Element[] = [];
      let parent = el?.parentElement;
      while (parent && parent !== document.body) {
        const { overflow, overflowY } = window.getComputedStyle(parent);
        if (['auto', 'scroll'].includes(overflow) || ['auto', 'scroll'].includes(overflowY)) {
          scrollableParents.push(parent);
        }
        parent = parent.parentElement;
      }

      scrollableParents.forEach((p) => p.addEventListener('scroll', handleScroll, { passive: true }));
      return () => scrollableParents.forEach((p) => p.removeEventListener('scroll', handleScroll));
    }, [isOverlayVisible]);

    const handleShow = useCallback(() => {
      setIsOverlayVisible(true);
      onShow?.();
    }, [onShow]);

    const handleHide = useCallback(() => {
      setIsOverlayVisible(false);
      onHide?.();
    }, [onHide]);

    return (
      <Dropdown
        ref={mergedRef}
        filter={filter}
        resetFilterOnHide
        emptyFilterMessage={<></>}
        filterClearIcon={(options) => (
          <TnkButton
            icon="pi pi-times"
            buttonType="custom"
            className="p-button-text btn-clear"
            onClick={(e) => {
              e.stopPropagation();
              options.iconProps.onClick?.(e);
            }}
            aria-label="Clear"
          />
        )}
        showFilterClear
        className={`tnk-dropdown${className ? ` ${className}` : ''}`}
        panelClassName={`tnk-dropdown-panel${panelClassName ? ` ${panelClassName}` : ''}`}
        onShow={handleShow}
        onHide={handleHide}
        {...props}
      />
    );
  }
);

TnkDropdown.displayName = 'TnkDropdown';

export default TnkDropdown;
